import { useCallback, useRef } from 'react';

const HOLD_MUSIC_URL = `${process.env.REACT_APP_S3_STATIC_IMAGE_URL}/audio/OnHoldAudio.mp3`;

export default function useTwilioHold(device: any, call: any) {
  const holdProcessorRef = useRef(null);

  /* -------------------------------------------------- */
  /* Ensure Device Audio */
  /* -------------------------------------------------- */

  const ensureDeviceAudio = useCallback(async () => {
    if (device?.audio) return device.audio;

    if (!device) {
      throw new Error('Twilio Device not ready');
    }

    return device.audio;
  }, [device]);

  /* -------------------------------------------------- */
  /* Create Hold Music Processor */
  /* -------------------------------------------------- */

  const createHoldMusicProcessor = (holdUrl) => {
    const AudioContextClass =
      window.AudioContext || (window as any)?.webkitAudioContext;

    const audioContext = new AudioContextClass();

    let sourceNode;
    let destinationNode;
    let audioElement;

    const cleanup = async () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.src = '';
        audioElement.load();
      }

      sourceNode?.disconnect();
      destinationNode?.disconnect();

      if (audioContext.state !== 'closed') {
        await audioContext.close();
      }
    };

    return {
      async createProcessedStream() {
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        audioElement = new Audio(holdUrl);
        audioElement.loop = true;
        audioElement.crossOrigin = 'anonymous';

        await new Promise((resolve, reject) => {
          audioElement.addEventListener('canplaythrough', resolve, {
            once: true,
          });
          audioElement.addEventListener('error', reject, {
            once: true,
          });
          audioElement.load();
        });

        sourceNode = audioContext.createMediaElementSource(audioElement);
        destinationNode = audioContext.createMediaStreamDestination();

        sourceNode.connect(destinationNode);

        await audioElement.play();

        return destinationNode.stream;
      },

      async destroyProcessedStream() {
        await cleanup();
      },
    };
  };

  /* -------------------------------------------------- */
  /* HOLD */
  /* -------------------------------------------------- */

  const holdCall = useCallback(async () => {
    if (!call || !device) return;

    const audioInterface = await ensureDeviceAudio();

    const processor = createHoldMusicProcessor(HOLD_MUSIC_URL);
    holdProcessorRef.current = processor;

    // Replace mic with hold music
    await audioInterface.addProcessor(processor);
  }, [call, device, ensureDeviceAudio]);

  /* -------------------------------------------------- */
  /* UNHOLD */
  /* -------------------------------------------------- */

  const unholdCall = useCallback(async () => {
    if (!call || !device) return;

    const audioInterface = await ensureDeviceAudio();

    if (holdProcessorRef.current) {
      await audioInterface.removeProcessor(holdProcessorRef.current);

      await holdProcessorRef.current.destroyProcessedStream();

      holdProcessorRef.current = null;
    }
  }, [call, device, ensureDeviceAudio]);

  const cleanupHold = useCallback(async () => {
    if (!holdProcessorRef.current) return;

    if (device?.audio) {
      try {
        await device.audio.removeProcessor(holdProcessorRef.current);
      } catch (e) {
        console.error('[Hold] Failed to remove processor during cleanup:', e);
      }
    }

    await holdProcessorRef.current.destroyProcessedStream();
    holdProcessorRef.current = null;
  }, [device]);

  if (!device || !call) {
    return {
      holdCall: () => {},
      unholdCall: () => {},
      cleanupHold,
    };
  }

  return { holdCall, unholdCall, cleanupHold };
}
